import { HttpClient } from '@angular/common/http';
import Cookies from 'js-cookie';
import { reduce } from 'lodash';
import { Observable, throwError, timer, of } from 'rxjs';
import { tap, distinctUntilChanged, switchMap, catchError, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { env } from '@envs/env';
import { config } from '@config/config';
import { UrlUtils } from '@app/utils/url.utils';
import { JwtService } from '@app/utils/jwt.service';
import { AuthState, AuthStatus, ChangeStatus, SetAuthData } from '@app/auth/auth.state';
import { trackEvent } from '@app/utils/amplitude.utils';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../utils/jwt.service";
import * as i3 from "@ngxs/store";
export class OAuthService {
    constructor(http, jwt, store) {
        this.http = http;
        this.jwt = jwt;
        this.store = store;
        this.timer$ = timer(0, env.auth.interval);
        this.trackEvent = trackEvent;
        /** Same proactive refresh window as supportal (seconds before access token expiry). */
        this.accessTokenRefreshBufferSeconds = 5 * 60;
        this.init();
    }
    // set up and watch authevents to trigger login acitons
    init() {
        this.authEvents$ = this.getAuthEvents();
        this.startAuthInterval();
        const token = this.getAccessToken();
        if (token) {
            this.setAccessToken(token);
        }
        // start watching
        this.subscribeToAuthEvents();
    }
    getAuthEvents() {
        return this.store.select(AuthState.status).pipe(distinctUntilChanged());
    }
    startAuthInterval() {
        this.timer$.pipe(switchMap(() => this.updateAuthState())).subscribe();
    }
    // watch auth events and update auth status
    subscribeToAuthEvents() {
        this.authEvents$.subscribe((status) => {
            if (status === AuthStatus.AUTH_VALID) {
                const token = this.getAccessToken();
                this.http.get(this.jwt.getBaseUrl(token)).subscribe((res) => {
                    this.store.dispatch(new SetAuthData(this.jwt.getBaseUrl(token), this.jwt.getScopes(token), res.data[0]));
                    this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_COMPLETE));
                });
            }
        });
    }
    updateAuthState() {
        const at = this.getAccessToken();
        const rt = this.getRefreshToken();
        const tokens = {
            at: at === '' ? false : true,
            rt: rt === '' ? false : true,
        };
        return this.authEvents$.pipe(take(1), tap((status) => {
            if (tokens.at === false && tokens.rt === false) {
                this.completeAuthorizationCodeFlow().subscribe({
                    error: (err) => {
                        let error;
                        if (typeof err === 'string') {
                            error = err;
                        }
                        else if (err && err.error && err.error.detailed[0]) {
                            if (err.error.message === 'unauthorized_client') {
                                error = 'Access Denied.';
                            }
                            else {
                                error = err.error.detailed[0];
                            }
                        }
                        this.initAuthorizationCodeFlow(error);
                    },
                });
            }
            else if (tokens.at === false && tokens.rt === true) {
                this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_NO_ACCESS));
                this.refreshAccessToken().subscribe();
            }
            else if (tokens.at === true && tokens.rt === false && status !== AuthStatus.AUTH_COMPLETE) {
                this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_NO_REFRESH));
                this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
            }
            else if (this.jwt.isTokenExpired(this.jwt.decodeToken(this.getAccessToken()), this.accessTokenRefreshBufferSeconds)) {
                this.refreshAccessToken().subscribe();
            }
            else if (status !== AuthStatus.AUTH_COMPLETE) {
                this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
            }
            else {
                this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_COMPLETE));
            }
        }));
    }
    isLoggedIn() {
        return !!this.getAccessToken();
    }
    initAuthorizationCodeFlow(error) {
        this.trackEvent('Auth Init Login');
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
        const state = this.generateState();
        this.saveState(state);
        const loginUrl = this.getStaticLoginUrl(state, error);
        if (!env.testing) {
            window.location.href = loginUrl;
        }
    }
    // Navigate to static login page
    getStaticLoginUrl(state, error) {
        let url = this.generateRedirectUrl(state, window.location.origin);
        if (error) {
            url += `&error=${error}`;
        }
        return url;
    }
    generateRedirectUrl(state, uri) {
        const url = `${env.apiUrl}/account/login?client_id=${env.auth.clientId}` +
            `&redirect_uri=${encodeURIComponent(uri)}&state=${state}&response_type=code`;
        return url;
    }
    completeAuthorizationCodeFlow() {
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_IN_PROGRESS));
        const params = UrlUtils.getUrlParams();
        if (!params.code || !params.state) {
            return throwError(null);
        }
        // Compare before clearing. Cookie/session was cleared on a prior attempt if the auth
        // interval fired again while the token exchange was still in flight (timer every 5s),
        // which produced a false "Invalid State".
        const storedState = this.peekState();
        if (params.state !== storedState) {
            trackEvent('Auth Invalid State');
            return throwError('Invalid State');
        }
        // Consume the callback URL immediately so repeat interval ticks do not re-enter with
        // code/state still in the address bar after stored state was cleared.
        this.stripOAuthParamsFromUrl();
        this.getAndClearState();
        return this.http
            .post(`${env.apiUrl}/oauth/token`, {
            client_id: env.auth.clientId,
            code: params.code,
            grant_type: 'authorization_code',
            redirect_uri: window.location.origin,
        })
            .pipe(tap((res) => {
            trackEvent('Auth Login Successful');
            const tokens = res.data[0];
            this.saveAccessToken(tokens.access_token, tokens.expires_in);
            this.saveRefreshToken(tokens.refresh_token);
            this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
        }, () => {
            trackEvent('Auth Login Unsuccessful');
            this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
        }));
    }
    refreshAccessToken() {
        this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_REFRESHING));
        const authParams = `grant_type=refresh_token&client_id=${env.auth.clientId}&refresh_token=${this.getRefreshToken()}`;
        return this.http
            .post(`${env.apiUrl}/oauth/token`, encodeURI(authParams), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }) // eslint-disable-line @typescript-eslint/indent
            .pipe(tap((res) => {
            trackEvent('Auth Token Refreshed');
            const tokens = res.data[0];
            this.saveAccessToken(tokens.access_token, tokens.expires_in);
            this.saveRefreshToken(tokens.refresh_token);
            this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_VALID));
        }, () => {
            this.store.dispatch(new ChangeStatus(AuthStatus.AUTH_INVALID));
        }));
    }
    /**
     * Revokes the session on the server (Bearer access token + JSON body with refresh_token).
     * Clears local tokens and starts login only after the request finishes.
     */
    logout() {
        const rt = this.getRefreshToken();
        const request$ = rt
            ? this.http.post(`${env.apiUrl}/oauth/logout`, { refresh_token: rt }, {
                headers: { 'Content-Type': 'application/json' },
            })
            : of(null);
        return request$.pipe(catchError(() => of(null)), tap(() => {
            this.clearTokens();
            this.initAuthorizationCodeFlow();
        }));
    }
    /**
     * Generates a random value for OAuth state
     * @returns Series of hexadecimal characters
     */
    getAccessToken() {
        return Cookies.get(env.auth.accessTokenName) || '';
    }
    getRefreshToken() {
        return Cookies.get(env.auth.refreshTokenName) || '';
    }
    clearTokens() {
        const opts = this.getAuthCookieOpts();
        Cookies.remove(env.auth.accessTokenName, opts);
        Cookies.remove(env.auth.refreshTokenName, opts);
    }
    setAccessToken(tok) {
        this.store.dispatch(new SetAuthData(this.jwt.getBaseUrl(tok), this.jwt.getScopes(tok), { id: this.jwt.getTokenId(tok) }));
    }
    generateState() {
        // Length is halved because there are 2 hex chars in 1 uint8
        const arr = new Uint8Array(config.authStateLength / 2);
        // Generate random values (msCrypto for IE 11)
        if (window.msCrypto) {
            window.msCrypto.getRandomValues(arr);
        }
        else {
            window.crypto.getRandomValues(arr);
        }
        // Converts random values to hexadecimal array, then joins into a string
        return reduce(arr, (acc, val) => acc + ('0' + val.toString(16)).slice(-2), '');
    }
    getBaseHost() {
        const parts = window.location.hostname.split('.');
        return [parts[parts.length - 2], parts[parts.length - 1]].join('.');
    }
    /** Shared attributes so set/remove stay in sync (incl. SameSite for CSRF-style hardening). */
    getAuthCookieOpts() {
        return {
            secure: env.auth.https,
            domain: env.auth.https ? `.${this.getBaseHost()}` : 'localhost',
            sameSite: 'lax',
        };
    }
    saveAccessToken(token, expiresIn) {
        Cookies.set(env.auth.accessTokenName, token, Object.assign({}, this.getAuthCookieOpts(), { expires: new Date(Date.now() + expiresIn * 1000) }));
    }
    saveRefreshToken(token) {
        Cookies.set(env.auth.refreshTokenName, token, Object.assign({}, this.getAuthCookieOpts(), { expires: env.auth.refreshTokenExpires }));
    }
    saveState(state) {
        // Per-tab storage avoids cross-tab races where a second login overwrites the shared
        // oauth_state cookie and the first tab returns with a mismatched state.
        sessionStorage.setItem(env.auth.stateTokenName, state);
    }
    peekState() {
        return sessionStorage.getItem(env.auth.stateTokenName) || Cookies.get(env.auth.stateTokenName) || '';
    }
    getAndClearState() {
        const key = env.auth.stateTokenName;
        const state = sessionStorage.getItem(key) || Cookies.get(key) || '';
        sessionStorage.removeItem(key);
        Cookies.remove(key, this.getAuthCookieOpts());
        return state;
    }
    /** Removes OAuth callback params so UrlUtils and repeat auth polling do not see a stale code/state pair. */
    stripOAuthParamsFromUrl() {
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            const path = url.pathname + url.search + url.hash;
            window.history.replaceState(window.history.state, document.title, path);
        }
        catch (_a) {
            // ignore replaceState errors in non-browser or exotic URLs
        }
    }
}
OAuthService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function OAuthService_Factory() { return new OAuthService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.JwtService), i0.ɵɵinject(i3.Store)); }, token: OAuthService, providedIn: "root" });
